import React, { useState, useCallback, useEffect } from "react";
import { AssetMetadata, Asset } from "@forgepoint/shared";
import { AssetFileViewer } from "./AssetFileViewer";
import { useAutoSave } from "@/modules/quotesDeprecated/QuotePreview/hooks/useAutoSave";
import {
  Loader2,
  Check,
  AlertCircle,
  Plus,
  Trash2,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { fetchApi } from "@/lib/api";
import { DrawingItemMetadata, ModelItemMetadata } from "@forgepoint/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

interface AssetEditorProps {
  asset: Asset;
  question?: string | null;
  onUpdate: (metadata: AssetMetadata) => void;
}

export function AssetEditor({ asset, question, onUpdate }: AssetEditorProps) {
  const [metadata, setMetadata] = useState<AssetMetadata | null>(null);
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [validationError, setValidationError] = useState<string | null>(null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  useEffect(() => {
    if (asset.metadata) {
      setMetadata(asset.metadata);
    }
  }, [asset.metadata]);

  const onSave = useCallback(
    async (updatedMetadata: AssetMetadata) => {
      try {
        const response = await fetchApi(`api/assets/${asset.id}`, "PATCH", {
          body: {
            metadata: updatedMetadata,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to save asset metadata");
        }

        setLastSaved(new Date());
        setValidationError(null);
        onUpdate(updatedMetadata);
      } catch (err) {
        setValidationError(
          err instanceof Error ? err.message : "Failed to save",
        );
      }
    },
    [asset.id, onUpdate],
  );

  const { autoSaving, queueEdit } = useAutoSave<AssetMetadata>({
    onSave,
    timeout: 1000,
  });

  const updateItem = useCallback(
    (index: number, updatedItem: DrawingItemMetadata | ModelItemMetadata) => {
      if (!metadata) {
        return;
      }

      if (metadata.type === "drawing") {
        const newItems = [...metadata.data.items];
        newItems[index] = updatedItem as DrawingItemMetadata;
        const newMetadata: AssetMetadata = {
          type: "drawing",
          data: { items: newItems },
        };
        setMetadata(newMetadata);
        queueEdit(newMetadata);
      } else {
        const newItems = [...metadata.data.items];
        newItems[index] = updatedItem as ModelItemMetadata;
        const newMetadata: AssetMetadata = {
          type: "model",
          data: { items: newItems },
        };
        setMetadata(newMetadata);
        queueEdit(newMetadata);
      }
    },
    [metadata, queueEdit],
  );

  const addItem = useCallback(() => {
    if (!metadata) {
      return;
    }

    if (metadata.type === "drawing") {
      const newItem: DrawingItemMetadata = {
        itemNumber: null,
        itemDescription: null,
        drawingNumber: null,
        revision: null,
        finish: null,
        material: null,
        materialThickness: null,
        assemblyComponents: [],
        shearSizeLength: null,
        shearSizeWidth: null,
        shearSizeUnits: null,
        castWeight: null,
        itemWeight: null,
        weightUnits: null,
        itemSizeLength: null,
        itemSizeWidth: null,
        itemSizeHeight: null,
        itemSizeUnits: null,
        surfaceArea: null,
        surfaceAreaUnits: null,
        notes: "",
      };
      const newMetadata: AssetMetadata = {
        type: "drawing",
        data: { items: [...metadata.data.items, newItem] },
      };
      setMetadata(newMetadata);
      queueEdit(newMetadata);
    } else {
      const newItem: ModelItemMetadata = {
        name: null,
        volume: null,
        volumeUnits: null,
        boundingBoxDimensions: null,
        boundingBoxUnits: null,
        externalSurfaceArea: null,
        wallThickness: null,
        draftAngles: null,
        assemblyComponents: [],
        notes: "",
      };
      const newMetadata: AssetMetadata = {
        type: "model",
        data: { items: [...metadata.data.items, newItem] },
      };
      setMetadata(newMetadata);
      queueEdit(newMetadata);
    }
  }, [metadata, queueEdit]);

  const removeItem = useCallback(
    (index: number) => {
      if (!metadata) {
        return;
      }

      if (metadata.type === "drawing") {
        const newMetadata: AssetMetadata = {
          type: "drawing",
          data: {
            items: metadata.data.items.filter((_, i) => i !== index),
          },
        };
        setMetadata(newMetadata);
        queueEdit(newMetadata);
      } else {
        const newMetadata: AssetMetadata = {
          type: "model",
          data: {
            items: metadata.data.items.filter((_, i) => i !== index),
          },
        };
        setMetadata(newMetadata);
        queueEdit(newMetadata);
      }
    },
    [metadata, queueEdit],
  );

  const toggleExpanded = useCallback(
    (index: number) => {
      const newExpandedItems = new Set(expandedItems);
      if (newExpandedItems.has(index)) {
        newExpandedItems.delete(index);
      } else {
        newExpandedItems.add(index);
      }
      setExpandedItems(newExpandedItems);
    },
    [expandedItems],
  );

  const getStatusMessage = () => {
    if (validationError) {
      return validationError;
    }
    if (autoSaving) {
      return "Saving...";
    }
    if (lastSaved) {
      const now = new Date();
      const diffSeconds = Math.floor(
        (now.getTime() - lastSaved.getTime()) / 1000,
      );
      if (diffSeconds < 60) {
        return "Saved";
      }
      return `Saved ${diffSeconds < 120 ? "1 minute ago" : `${Math.floor(diffSeconds / 60)} minutes ago`}`;
    }
    return null;
  };

  const statusMessage = getStatusMessage();

  if (!metadata) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">
          No metadata available for this asset
        </p>
      </div>
    );
  }

  const items = metadata.data.items;

  return (
    <div className="flex h-full">
      {/* Left side - File Viewer */}
      <div className="w-1/2 border-r flex items-center justify-center bg-muted/20">
        <AssetFileViewer asset={asset} />
      </div>

      {/* Right side - Metadata Editor */}
      <div className="w-1/2 flex flex-col">
        {statusMessage && (
          <div
            className={cn(
              "flex items-center gap-2 px-4 py-2 text-sm border-b",
              validationError
                ? "bg-red-50 text-red-700 border-red-200"
                : autoSaving
                  ? "bg-blue-50 text-blue-700 border-blue-200"
                  : "bg-green-50 text-green-700 border-green-200",
            )}
          >
            {validationError ? (
              <AlertCircle className="h-4 w-4" />
            ) : autoSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" />
            )}
            <span>{statusMessage}</span>
          </div>
        )}
        <div className="flex-1 overflow-auto">
          <div className="flex items-center justify-between mb-6 sticky top-0 bg-background z-20 pb-4 border-b p-6">
            <h3 className="text-lg font-semibold">
              {items.length} {items.length === 1 ? "Item" : "Items"}
            </h3>
            <Button onClick={addItem} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </Button>
          </div>

          <div className="space-y-4">
            {items.length > 0 ? (
              metadata.type === "drawing" ? (
                metadata.data.items.map((item, itemIndex) => (
                  <DrawingItemCard
                    key={itemIndex}
                    item={item}
                    isExpanded={expandedItems.has(itemIndex)}
                    question={question}
                    onUpdateItem={(updatedItem) =>
                      updateItem(itemIndex, updatedItem)
                    }
                    onRemoveItem={() => removeItem(itemIndex)}
                    onToggleExpanded={() => toggleExpanded(itemIndex)}
                  />
                ))
              ) : (
                metadata.data.items.map((item, itemIndex) => (
                  <ModelItemCard
                    key={itemIndex}
                    item={item}
                    isExpanded={expandedItems.has(itemIndex)}
                    question={question}
                    onUpdateItem={(updatedItem) =>
                      updateItem(itemIndex, updatedItem)
                    }
                    onRemoveItem={() => removeItem(itemIndex)}
                    onToggleExpanded={() => toggleExpanded(itemIndex)}
                  />
                ))
              )
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <p>No items yet. Click "Add Item" to get started.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface DrawingItemCardProps {
  item: DrawingItemMetadata;
  isExpanded: boolean;
  question?: string | null;
  onUpdateItem: (updatedItem: DrawingItemMetadata) => void;
  onRemoveItem: () => void;
  onToggleExpanded: () => void;
}

const FieldGroup = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
    {children}
  </div>
);

function DrawingItemCard({
  item,
  isExpanded,
  question,
  onUpdateItem,
  onRemoveItem,
  onToggleExpanded,
}: DrawingItemCardProps) {
  const updateField = useCallback(
    (
      field: string,
      value: string | number | null | Record<string, unknown>,
    ) => {
      onUpdateItem({ ...item, [field]: value });
    },
    [item, onUpdateItem],
  );

  const addAssemblyComponent = useCallback(() => {
    const newComponent = {
      quantityPerAssembly: null,
      description: null,
      drawingNumber: null,
      itemNumber: null,
    };
    onUpdateItem({
      ...item,
      assemblyComponents: [...item.assemblyComponents, newComponent],
    });
  }, [item, onUpdateItem]);

  const removeAssemblyComponent = useCallback(
    (componentIndex: number) => {
      const newComponents = item.assemblyComponents.filter(
        (_, i) => i !== componentIndex,
      );
      onUpdateItem({ ...item, assemblyComponents: newComponents });
    },
    [item, onUpdateItem],
  );

  const updateAssemblyComponent = useCallback(
    (componentIndex: number, field: string, value: string | null) => {
      const newComponents = [...item.assemblyComponents];
      (newComponents[componentIndex] as Record<string, unknown>)[field] = value;
      onUpdateItem({ ...item, assemblyComponents: newComponents });
    },
    [item, onUpdateItem],
  );
  return (
    <div className="border rounded-lg bg-card shadow-sm hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-muted/30">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">
              {item.itemNumber || item.itemDescription || "Untitled Item"}
            </span>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={onRemoveItem}
        >
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Basic Info */}
        <div className="grid grid-cols-2 gap-3">
          <FieldGroup label="Item Number">
            <Input
              value={item.itemNumber || ""}
              onChange={(e) =>
                updateField("itemNumber", e.target.value || null)
              }
              placeholder="Enter item number"
            />
          </FieldGroup>
          <FieldGroup label="Drawing Number">
            <Input
              value={item.drawingNumber || ""}
              onChange={(e) =>
                updateField("drawingNumber", e.target.value || null)
              }
              placeholder="Enter drawing number"
            />
          </FieldGroup>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FieldGroup label="Description">
            <Input
              value={item.itemDescription || ""}
              onChange={(e) =>
                updateField("itemDescription", e.target.value || null)
              }
              placeholder="Enter description"
            />
          </FieldGroup>
          <FieldGroup label="Revision">
            <Input
              value={item.revision || ""}
              onChange={(e) => updateField("revision", e.target.value || null)}
              placeholder="Enter revision"
            />
          </FieldGroup>
        </div>

        {/* Material & Finish */}
        <div className="grid grid-cols-2 gap-3">
          <FieldGroup label="Material">
            <Input
              value={item.material || ""}
              onChange={(e) => updateField("material", e.target.value || null)}
              placeholder="Enter material"
            />
          </FieldGroup>
          <FieldGroup label="Finish">
            <Input
              value={item.finish || ""}
              onChange={(e) => updateField("finish", e.target.value || null)}
              placeholder="Enter finish"
            />
          </FieldGroup>
        </div>

        {/* Dimensions */}
        <div className="grid grid-cols-2 gap-3">
          <FieldGroup label="Item Size (L × W × H)">
            <div className="flex gap-2">
              <Input
                value={item.itemSizeLength || ""}
                onChange={(e) =>
                  updateField("itemSizeLength", e.target.value || null)
                }
                placeholder="L"
                className="flex-1"
              />
              <Input
                value={item.itemSizeWidth || ""}
                onChange={(e) =>
                  updateField("itemSizeWidth", e.target.value || null)
                }
                placeholder="W"
                className="flex-1"
              />
              <Input
                value={item.itemSizeHeight || ""}
                onChange={(e) =>
                  updateField("itemSizeHeight", e.target.value || null)
                }
                placeholder="H"
                className="flex-1"
              />
            </div>
          </FieldGroup>
          <FieldGroup label="Units">
            <Input
              value={item.itemSizeUnits || ""}
              onChange={(e) =>
                updateField("itemSizeUnits", e.target.value || null)
              }
              placeholder="e.g., in, mm"
            />
          </FieldGroup>
        </div>

        {/* Shear Size */}
        <div className="grid grid-cols-2 gap-3">
          <FieldGroup label="Shear Size (L × W)">
            <div className="flex gap-2">
              <Input
                value={item.shearSizeLength || ""}
                onChange={(e) =>
                  updateField("shearSizeLength", e.target.value || null)
                }
                placeholder="Length"
                className="flex-1"
              />
              <Input
                value={item.shearSizeWidth || ""}
                onChange={(e) =>
                  updateField("shearSizeWidth", e.target.value || null)
                }
                placeholder="Width"
                className="flex-1"
              />
            </div>
          </FieldGroup>
          <FieldGroup label="Units">
            <Input
              value={item.shearSizeUnits || ""}
              onChange={(e) =>
                updateField("shearSizeUnits", e.target.value || null)
              }
              placeholder="e.g., in, mm"
            />
          </FieldGroup>
        </div>

        {/* Weights */}
        <div className="grid grid-cols-3 gap-3">
          <FieldGroup label="Cast Weight">
            <Input
              value={item.castWeight || ""}
              onChange={(e) =>
                updateField("castWeight", e.target.value || null)
              }
              placeholder="Enter weight"
            />
          </FieldGroup>
          <FieldGroup label="Item Weight">
            <Input
              value={item.itemWeight || ""}
              onChange={(e) =>
                updateField("itemWeight", e.target.value || null)
              }
              placeholder="Enter weight"
            />
          </FieldGroup>
          <FieldGroup label="Units">
            <Input
              value={item.weightUnits || ""}
              onChange={(e) =>
                updateField("weightUnits", e.target.value || null)
              }
              placeholder="e.g., lbs, kg"
            />
          </FieldGroup>
        </div>

        {/* Surface Area */}
        <div className="grid grid-cols-2 gap-3">
          <FieldGroup label="Surface Area">
            <Input
              value={item.surfaceArea || ""}
              onChange={(e) =>
                updateField("surfaceArea", e.target.value || null)
              }
              placeholder="Enter surface area"
            />
          </FieldGroup>
          <FieldGroup label="Units">
            <Input
              value={item.surfaceAreaUnits || ""}
              onChange={(e) =>
                updateField("surfaceAreaUnits", e.target.value || null)
              }
              placeholder="e.g., in², mm²"
            />
          </FieldGroup>
        </div>

        {/* Notes */}
        <FieldGroup label={question || "Notes"}>
          <Input
            value={item.notes || ""}
            onChange={(e) => updateField("notes", e.target.value || null)}
            placeholder={`Enter ${question?.toLowerCase() || "notes"}`}
          />
        </FieldGroup>

        {/* Assembly Components */}
        <div className="pt-2 border-t">
          <div className="flex items-center justify-between mb-3">
            <Label className="text-sm font-medium">Assembly Components</Label>
            <div className="flex gap-2">
              {item.assemblyComponents.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addAssemblyComponent}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={onToggleExpanded}>
                {isExpanded ? (
                  <>
                    <ChevronDown className="h-4 w-4 mr-1" />
                    Hide
                  </>
                ) : (
                  <>
                    <ChevronRight className="h-4 w-4 mr-1" />
                    Show{" "}
                    {item.assemblyComponents.length > 0
                      ? `(${item.assemblyComponents.length})`
                      : ""}
                  </>
                )}
              </Button>
            </div>
          </div>

          {isExpanded && (
            <div className="space-y-2">
              {item.assemblyComponents.length > 0 ? (
                item.assemblyComponents.map((component, componentIndex) => (
                  <div
                    key={componentIndex}
                    className="p-3 border rounded-lg bg-muted/20 space-y-2"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-muted-foreground">
                        Component #{componentIndex + 1}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => removeAssemblyComponent(componentIndex)}
                      >
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <FieldGroup label="Quantity">
                        <Input
                          value={component.quantityPerAssembly || ""}
                          onChange={(e) =>
                            updateAssemblyComponent(
                              componentIndex,
                              "quantityPerAssembly",
                              e.target.value || null,
                            )
                          }
                          placeholder="Qty"
                          className="h-8"
                        />
                      </FieldGroup>
                      <FieldGroup label="Item #">
                        <Input
                          value={component.itemNumber || ""}
                          onChange={(e) =>
                            updateAssemblyComponent(
                              componentIndex,
                              "itemNumber",
                              e.target.value || null,
                            )
                          }
                          placeholder="Item #"
                          className="h-8"
                        />
                      </FieldGroup>
                    </div>
                    <FieldGroup label="Description">
                      <Input
                        value={component.description || ""}
                        onChange={(e) =>
                          updateAssemblyComponent(
                            componentIndex,
                            "description",
                            e.target.value || null,
                          )
                        }
                        placeholder="Description"
                        className="h-8"
                      />
                    </FieldGroup>
                    <FieldGroup label="Drawing #">
                      <Input
                        value={component.drawingNumber || ""}
                        onChange={(e) =>
                          updateAssemblyComponent(
                            componentIndex,
                            "drawingNumber",
                            e.target.value || null,
                          )
                        }
                        placeholder="Drawing #"
                        className="h-8"
                      />
                    </FieldGroup>
                  </div>
                ))
              ) : (
                <div className="text-center py-6">
                  <p className="text-sm text-muted-foreground mb-2">
                    No assembly components
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addAssemblyComponent}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Component
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface ModelItemCardProps {
  item: ModelItemMetadata;
  isExpanded: boolean;
  question?: string | null;
  onUpdateItem: (updatedItem: ModelItemMetadata) => void;
  onRemoveItem: () => void;
  onToggleExpanded: () => void;
}

function ModelItemCard({
  item,
  isExpanded,
  question,
  onUpdateItem,
  onRemoveItem,
  onToggleExpanded,
}: ModelItemCardProps) {
  const updateField = useCallback(
    (
      field: string,
      value: string | number | null | Record<string, unknown>,
    ) => {
      onUpdateItem({ ...item, [field]: value });
    },
    [item, onUpdateItem],
  );

  const addAssemblyComponent = useCallback(() => {
    const newComponent = {
      name: null,
      quantity: null,
    };
    onUpdateItem({
      ...item,
      assemblyComponents: [...item.assemblyComponents, newComponent],
    });
  }, [item, onUpdateItem]);

  const removeAssemblyComponent = useCallback(
    (componentIndex: number) => {
      const newComponents = item.assemblyComponents.filter(
        (_, i) => i !== componentIndex,
      );
      onUpdateItem({ ...item, assemblyComponents: newComponents });
    },
    [item, onUpdateItem],
  );

  const updateAssemblyComponent = useCallback(
    (componentIndex: number, field: string, value: string | number | null) => {
      const newComponents = [...item.assemblyComponents];
      (newComponents[componentIndex] as Record<string, unknown>)[field] = value;
      onUpdateItem({ ...item, assemblyComponents: newComponents });
    },
    [item, onUpdateItem],
  );
  return (
    <div className="border rounded-lg bg-card shadow-sm hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-muted/30">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">
              {item.name || "Untitled Model"}
            </span>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={onRemoveItem}
        >
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Basic Info */}
        <FieldGroup label="Name">
          <Input
            value={item.name || ""}
            onChange={(e) => updateField("name", e.target.value || null)}
            placeholder="Enter name"
          />
        </FieldGroup>

        {/* Volume */}
        <div className="grid grid-cols-2 gap-3">
          <FieldGroup label="Volume">
            <Input
              type="number"
              value={item.volume || ""}
              onChange={(e) =>
                updateField(
                  "volume",
                  e.target.value ? parseFloat(e.target.value) : null,
                )
              }
              placeholder="Enter volume"
            />
          </FieldGroup>
          <FieldGroup label="Units">
            <Input
              value={item.volumeUnits || ""}
              onChange={(e) =>
                updateField("volumeUnits", e.target.value || null)
              }
              placeholder="e.g., in³, mm³"
            />
          </FieldGroup>
        </div>

        {/* Bounding Box */}
        <div className="grid grid-cols-2 gap-3">
          <FieldGroup label="Bounding Box (X × Y × Z)">
            <div className="flex gap-2">
              <Input
                type="number"
                value={item.boundingBoxDimensions?.x || ""}
                onChange={(e) => {
                  const value = e.target.value
                    ? parseFloat(e.target.value)
                    : null;
                  const newDimensions = {
                    ...(item.boundingBoxDimensions || {
                      x: null,
                      y: null,
                      z: null,
                    }),
                    x: value,
                  };
                  updateField("boundingBoxDimensions", newDimensions);
                }}
                placeholder="X"
                className="flex-1"
              />
              <Input
                type="number"
                value={item.boundingBoxDimensions?.y || ""}
                onChange={(e) => {
                  const value = e.target.value
                    ? parseFloat(e.target.value)
                    : null;
                  const newDimensions = {
                    ...(item.boundingBoxDimensions || {
                      x: null,
                      y: null,
                      z: null,
                    }),
                    y: value,
                  };
                  updateField("boundingBoxDimensions", newDimensions);
                }}
                placeholder="Y"
                className="flex-1"
              />
              <Input
                type="number"
                value={item.boundingBoxDimensions?.z || ""}
                onChange={(e) => {
                  const value = e.target.value
                    ? parseFloat(e.target.value)
                    : null;
                  const newDimensions = {
                    ...(item.boundingBoxDimensions || {
                      x: null,
                      y: null,
                      z: null,
                    }),
                    z: value,
                  };
                  updateField("boundingBoxDimensions", newDimensions);
                }}
                placeholder="Z"
                className="flex-1"
              />
            </div>
          </FieldGroup>
          <FieldGroup label="Units">
            <Input
              value={item.boundingBoxUnits || ""}
              onChange={(e) =>
                updateField("boundingBoxUnits", e.target.value || null)
              }
              placeholder="e.g., in, mm"
            />
          </FieldGroup>
        </div>

        {/* Surface Area */}
        <FieldGroup label="External Surface Area">
          <Input
            type="number"
            value={item.externalSurfaceArea || ""}
            onChange={(e) =>
              updateField(
                "externalSurfaceArea",
                e.target.value ? parseFloat(e.target.value) : null,
              )
            }
            placeholder="Enter surface area"
          />
        </FieldGroup>

        {/* Wall Thickness */}
        <div className="grid grid-cols-4 gap-3">
          <FieldGroup label="Min Thickness">
            <Input
              type="number"
              value={item.wallThickness?.min || ""}
              onChange={(e) => {
                const value = e.target.value
                  ? parseFloat(e.target.value)
                  : null;
                const newThickness = {
                  ...(item.wallThickness || {
                    min: null,
                    max: null,
                    p10: null,
                    p90: null,
                    histogram: null,
                  }),
                  min: value,
                };
                updateField("wallThickness", newThickness);
              }}
              placeholder="Min"
            />
          </FieldGroup>
          <FieldGroup label="Max Thickness">
            <Input
              type="number"
              value={item.wallThickness?.max || ""}
              onChange={(e) => {
                const value = e.target.value
                  ? parseFloat(e.target.value)
                  : null;
                const newThickness = {
                  ...(item.wallThickness || {
                    min: null,
                    max: null,
                    p10: null,
                    p90: null,
                    histogram: null,
                  }),
                  max: value,
                };
                updateField("wallThickness", newThickness);
              }}
              placeholder="Max"
            />
          </FieldGroup>
          <FieldGroup label="P10">
            <Input
              type="number"
              value={item.wallThickness?.p10 || ""}
              onChange={(e) => {
                const value = e.target.value
                  ? parseFloat(e.target.value)
                  : null;
                const newThickness = {
                  ...(item.wallThickness || {
                    min: null,
                    max: null,
                    p10: null,
                    p90: null,
                    histogram: null,
                  }),
                  p10: value,
                };
                updateField("wallThickness", newThickness);
              }}
              placeholder="P10"
            />
          </FieldGroup>
          <FieldGroup label="P90">
            <Input
              type="number"
              value={item.wallThickness?.p90 || ""}
              onChange={(e) => {
                const value = e.target.value
                  ? parseFloat(e.target.value)
                  : null;
                const newThickness = {
                  ...(item.wallThickness || {
                    min: null,
                    max: null,
                    p10: null,
                    p90: null,
                    histogram: null,
                  }),
                  p90: value,
                };
                updateField("wallThickness", newThickness);
              }}
              placeholder="P90"
            />
          </FieldGroup>
        </div>

        {/* Draft Angles */}
        <div className="grid grid-cols-3 gap-3">
          <FieldGroup label="Negative Draft">
            <Input
              value={
                item.draftAngles?.hasNegativeDraft !== null
                  ? String(item.draftAngles?.hasNegativeDraft)
                  : ""
              }
              onChange={(e) => {
                const value =
                  e.target.value === "true"
                    ? true
                    : e.target.value === "false"
                      ? false
                      : null;
                const newDraftAngles = {
                  ...(item.draftAngles || {
                    hasNegativeDraft: null,
                    undercuts: null,
                    minDraftAngle: null,
                  }),
                  hasNegativeDraft: value,
                };
                updateField("draftAngles", newDraftAngles);
              }}
              placeholder="true/false"
            />
          </FieldGroup>
          <FieldGroup label="Undercuts">
            <Input
              type="number"
              value={item.draftAngles?.undercuts || ""}
              onChange={(e) => {
                const value = e.target.value
                  ? parseFloat(e.target.value)
                  : null;
                const newDraftAngles = {
                  ...(item.draftAngles || {
                    hasNegativeDraft: null,
                    undercuts: null,
                    minDraftAngle: null,
                  }),
                  undercuts: value,
                };
                updateField("draftAngles", newDraftAngles);
              }}
              placeholder="Count"
            />
          </FieldGroup>
          <FieldGroup label="Min Draft Angle">
            <Input
              type="number"
              value={item.draftAngles?.minDraftAngle || ""}
              onChange={(e) => {
                const value = e.target.value
                  ? parseFloat(e.target.value)
                  : null;
                const newDraftAngles = {
                  ...(item.draftAngles || {
                    hasNegativeDraft: null,
                    undercuts: null,
                    minDraftAngle: null,
                  }),
                  minDraftAngle: value,
                };
                updateField("draftAngles", newDraftAngles);
              }}
              placeholder="Angle"
            />
          </FieldGroup>
        </div>

        {/* Notes */}
        <FieldGroup label={question || "Notes"}>
          <Input
            value={item.notes || ""}
            onChange={(e) => updateField("notes", e.target.value || null)}
            placeholder={`Enter ${question?.toLowerCase() || "notes"}`}
          />
        </FieldGroup>

        {/* Assembly Components */}
        <div className="pt-2 border-t">
          <div className="flex items-center justify-between mb-3">
            <Label className="text-sm font-medium">Assembly Components</Label>
            <div className="flex gap-2">
              {item.assemblyComponents.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addAssemblyComponent}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={onToggleExpanded}>
                {isExpanded ? (
                  <>
                    <ChevronDown className="h-4 w-4 mr-1" />
                    Hide
                  </>
                ) : (
                  <>
                    <ChevronRight className="h-4 w-4 mr-1" />
                    Show{" "}
                    {item.assemblyComponents.length > 0
                      ? `(${item.assemblyComponents.length})`
                      : ""}
                  </>
                )}
              </Button>
            </div>
          </div>

          {isExpanded && (
            <div className="space-y-2">
              {item.assemblyComponents.length > 0 ? (
                item.assemblyComponents.map((component, componentIndex) => (
                  <div
                    key={componentIndex}
                    className="p-3 border rounded-lg bg-muted/20 space-y-2"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-muted-foreground">
                        Component #{componentIndex + 1}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => removeAssemblyComponent(componentIndex)}
                      >
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <FieldGroup label="Name">
                        <Input
                          value={component.name || ""}
                          onChange={(e) =>
                            updateAssemblyComponent(
                              componentIndex,
                              "name",
                              e.target.value || null,
                            )
                          }
                          placeholder="Name"
                          className="h-8"
                        />
                      </FieldGroup>
                      <FieldGroup label="Quantity">
                        <Input
                          type="number"
                          value={component.quantity || ""}
                          onChange={(e) =>
                            updateAssemblyComponent(
                              componentIndex,
                              "quantity",
                              e.target.value
                                ? parseFloat(e.target.value)
                                : null,
                            )
                          }
                          placeholder="Qty"
                          className="h-8"
                        />
                      </FieldGroup>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-6">
                  <p className="text-sm text-muted-foreground mb-2">
                    No assembly components
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addAssemblyComponent}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Component
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
